import React, { useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { Dashboard } from 'components/dashboard'
import { AddUser, EditUser, Profile, UserList } from 'components/users'
import { AddCustomer, EditCustomer, CustomerList } from 'components/customers'
import { AddShippingOption, EditShippingOption, ShippingOptionList } from 'components/system/shipping-options'
import { AddExistingSignOption, EditExistingSignOption, ExistingSignOptionList } from 'components/system/existing-sign-options'
import { AddInstallationOption, EditInstallationOption, InstallationOptionList } from 'components/system/installation-options'
import { AddSiteSurveyOption, EditSiteSurveyOption, SiteSurveyOptionList } from 'components/system/site-survey-options'
import { AddCommunicationOption, EditCommunicationOption, CommunicationOptionList } from 'components/system/communication-options'
import { AddPedestalDecorationOption, EditPedestalDecorationOption, PedestalDecorationOptionList } from 'components/system/pedestal-decoration-options'
import { AddVerticalSupportOption, EditVerticalSupportOption, VerticalSupportOptionList } from 'components/system/vertical-support-options'
import { AddEngineeringTableRow, EditEngineeringTableRow, EngineeringTableList } from 'components/system/vertical-support-engineering'
import { AddFooterStyleOption, EditFooterStyleOption, FooterStyleOptionList } from 'components/system/footer-style-options'
import { AddDisplayPricing, DisplayPricingList, EditDisplayPricing } from 'components/system/display-pricings'
import { BackWrapSideVentPricingForm, CabinetPricingForm, MowCurbPricingForm, PedestalPricingForm } from 'components/system/static-pricings-config'
import { AddEmcVendor, EditEmcVendor, EmcVendorList } from 'components/system/emc-vendor'
import { AddFabricationVendor, EditFabricationVendor, FabricationVendorList } from 'components/system/fabrication-vendor'
import { AddInstallationVendor, EditInstallationVendor, InstallationVendorList } from 'components/system/installation-vendor'
import { AddShippingVendor, EditShippingVendor, ShippingVendorList } from 'components/system/shipping-vendor'
import { AddEdgeQuote, EdgeQuoteList } from 'components/quotes/edge-quotes'
import { ProjectTaskTemplate } from 'components/projects/task-templates'
import { PROJECT_TASK_TEMPLATE_TYPES, ROLES } from 'lib'
import { AddAdhocQuote, AdhocQuoteList } from 'components/quotes/adhoc-quotes'
import { ProjectList } from 'components/projects/all-projects'
import { PROJECT_DETAIL_TABS, ProjectDetail } from 'components/projects/project-detail'
import { Commission, Overage } from 'components/sales'
import { ReportHistory } from 'components/sales/report-history/ReportHistory'
import { Metrics } from 'components/reporting/metrics/Metrics'
import { ProjectCustomerReport } from 'components/projects/customer-report'
import { ProjectTeamTaskReport } from 'components/projects/team-task-report'
import { ProjectTeamTaskReportIndividual } from 'components/projects/team-task-report/ProjectTeamTaskReportIndividual'
import { MetricsNoActivity, MetricsTaskDue } from 'components/reporting'
import { MY_TASKS_PAGE_TYPE, MyTasks } from 'components/projects/my-tasks/MyTasks'
import { AddArtworkRequest } from 'components/artwork-requests/AddArtworkRequest'
import { ArtworkRequestQueue } from 'components/artwork-requests/ArtworkRequestQueue'
import { ViewArtworkRequest } from 'components/artwork-requests/ViewArtworkRequest'
import { EditOpportunity, OpportunityList, ViewOpportunity } from 'components/opportunities'
import { DisplayPricingsConfig } from 'components/system/display-pricings-config'
import { SurveyEmailsConfig } from 'components/system/survey-emails-config'
import { MapView } from 'components/map'
import { LegacyProjectImport } from 'components/projects/legacy-import'

const arrRoles = Object.keys(ROLES).map(v => ROLES[v])

export const useRoutes = () => {
  return useMemo(() => {
    return [
      {
        path: '/',
        element: <Navigate to="/dashboard" replace={true} />,
        exact: true,
        replace: true,
        roles: [...arrRoles]
      },
      {
        path: '/dashboard',
        element: <Dashboard />,
        exact: true,
        roles: [...arrRoles]
      },
      // Customers
      {
        path: '/customers',
        element: <CustomerList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      {
        path: '/customers/add',
        element: <AddCustomer />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      {
        path: '/customers/edit/:customerId',
        element: <EditCustomer />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      // Opportunities
      {
        path: '/opportunities',
        element: <OpportunityList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      // {
      //   path: '/customers/add',
      //   element: <AddCustomer />,
      //   exact: true,
      //   roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      // },
      {
        path: '/opportunities/edit/:opportunityId',
        element: <EditOpportunity />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      {
        path: '/opportunities/view/:opportunityId',
        element: <ViewOpportunity />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      // Artwork Requests
      {
        path: '/artwork-requests/add',
        element: <AddArtworkRequest />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager]
      },
      {
        path: '/artwork-requests/view/:requestId',
        element: <ViewArtworkRequest />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager, ROLES.GraphicDesigner, ROLES.SeniorGraphicDesigner]
      },
      {
        path: '/artwork-requests/queue',
        element: <ArtworkRequestQueue />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager, ROLES.GraphicDesigner, ROLES.SeniorGraphicDesigner]
      },
      // Standard Quotes (Edge Quotes)
      {
        path: '/quotes/edge-quotes',
        element: <EdgeQuoteList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager]
      },
      {
        path: '/quotes/edge-quotes/add',
        element: <AddEdgeQuote />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager]
      },
      // Adhoc Quotes
      {
        path: '/quotes/adhoc-quotes',
        element: <AdhocQuoteList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager]
      },
      {
        path: '/quotes/adhoc-quotes/add',
        element: <AddAdhocQuote />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager]
      },
      // Projects - Customer Report
      {
        path: '/projects/customer-report',
        element: <ProjectCustomerReport />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Projects - Team Task Report
      {
        path: '/projects/team-task-report',
        element: <ProjectTeamTaskReport />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Projects - Team Task Report Individual
      {
        path: '/projects/team-task-report/:pmId',
        element: <ProjectTeamTaskReportIndividual />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Projects - Task Templates
      {
        path: '/projects/task-templates/general',
        element: <ProjectTaskTemplate type={PROJECT_TASK_TEMPLATE_TYPES.General} />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/projects/task-templates/client',
        element: <ProjectTaskTemplate type={PROJECT_TASK_TEMPLATE_TYPES.Client} />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/projects/legacy-import',
        element: <LegacyProjectImport />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SuperUser]
      },
      // Projects
      {
        path: '/projects',
        element: <ProjectList isMy={false} />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.SalesUser && v !== ROLES.ClientUser && v !== ROLES.GraphicDesigner))
      },
      // My Tasks
      {
        path: '/projects/my-tasks',
        element: <MyTasks pageType={MY_TASKS_PAGE_TYPE.All} />,
        exact: true,
        roles: arrRoles.filter(v => v !== ROLES.GraphicDesigner)
      },
      {
        path: '/projects/my-tasks/overdue',
        element: <MyTasks pageType={MY_TASKS_PAGE_TYPE.Overdue} />,
        exact: true,
        roles: arrRoles.filter(v => v !== ROLES.GraphicDesigner)
      },
      {
        path: '/projects/my-tasks/due-today',
        element: <MyTasks pageType={MY_TASKS_PAGE_TYPE.Duetoday} />,
        exact: true,
        roles: arrRoles.filter(v => v !== ROLES.GraphicDesigner)
      },
      // My Projects
      {
        path: '/projects/me',
        element: <ProjectList isMy={true} />,
        exact: true,
        roles: [ROLES.SalesUser, ROLES.ClientUser]
      },
      // Project Detail Page
      {
        path: '/projects/detail/:projectId',
        element: <ProjectDetail selectedTab={PROJECT_DETAIL_TABS.Detail} />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.GraphicDesigner))
      },
      {
        path: `/projects/detail/:projectId/${PROJECT_DETAIL_TABS.Detail.toLowerCase()}`,
        element: <ProjectDetail selectedTab={PROJECT_DETAIL_TABS.Detail} />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.GraphicDesigner))
      },
      {
        path: `/projects/detail/:projectId/${PROJECT_DETAIL_TABS.Overview.toLowerCase()}`,
        element: <ProjectDetail selectedTab={PROJECT_DETAIL_TABS.Overview} />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.GraphicDesigner))
      },
      {
        path: `/projects/detail/:projectId/${PROJECT_DETAIL_TABS.Tasks.toLowerCase()}`,
        element: <ProjectDetail selectedTab={PROJECT_DETAIL_TABS.Tasks} />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.GraphicDesigner))
      },
      {
        path: `/projects/detail/:projectId/${PROJECT_DETAIL_TABS.Contacts.toLowerCase()}`,
        element: <ProjectDetail selectedTab={PROJECT_DETAIL_TABS.Contacts} />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.GraphicDesigner))
      },
      // Map
      {
        path: '/map',
        element: <MapView />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser, ROLES.ProjectManager, ROLES.DirectorOfProjectManagement]
      },
      // Users
      {
        path: '/system/users',
        element: <UserList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.DirectorOfProjectManagement]
      },
      {
        path: '/system/users/add',
        element: <AddUser />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.DirectorOfProjectManagement]
      },
      {
        path: '/system/users/edit/:userId',
        element: <EditUser />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.DirectorOfProjectManagement]
      },
      {
        path: '/system/profile',
        element: <Profile />,
        exact: true,
        roles: [...arrRoles]
      },
      // Shipping Options
      {
        path: '/system/shipping-options',
        element: <ShippingOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/shipping-options/add',
        element: <AddShippingOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/shipping-options/edit/:optionId',
        element: <EditShippingOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Existing Sign Options
      {
        path: '/system/existing-sign-options',
        element: <ExistingSignOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/existing-sign-options/add',
        element: <AddExistingSignOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/existing-sign-options/edit/:optionId',
        element: <EditExistingSignOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Installation Options
      {
        path: '/system/installation-options',
        element: <InstallationOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/installation-options/add',
        element: <AddInstallationOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/installation-options/edit/:optionId',
        element: <EditInstallationOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Site Survey Options
      {
        path: '/system/site-survey-options',
        element: <SiteSurveyOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/site-survey-options/add',
        element: <AddSiteSurveyOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/site-survey-options/edit/:optionId',
        element: <EditSiteSurveyOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Communication Options
      {
        path: '/system/communication-options',
        element: <CommunicationOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/communication-options/add',
        element: <AddCommunicationOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/communication-options/edit/:optionId',
        element: <EditCommunicationOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Pedestal Decoration Options
      {
        path: '/system/pedestal-decoration-options',
        element: <PedestalDecorationOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/pedestal-decoration-options/add',
        element: <AddPedestalDecorationOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/pedestal-decoration-options/edit/:optionId',
        element: <EditPedestalDecorationOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Vertical Support Options
      {
        path: '/system/vertical-support-options',
        element: <VerticalSupportOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/vertical-support-options/add',
        element: <AddVerticalSupportOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/vertical-support-options/edit/:optionId',
        element: <EditVerticalSupportOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Vertical Support Engineering Table
      {
        path: '/system/vertical-support-engineering',
        element: <EngineeringTableList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/vertical-support-engineering/add',
        element: <AddEngineeringTableRow />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/vertical-support-engineering/edit/:rowId',
        element: <EditEngineeringTableRow />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Footer Style Options
      {
        path: '/system/footer-style-options',
        element: <FooterStyleOptionList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/footer-style-options/add',
        element: <AddFooterStyleOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/footer-style-options/edit/:optionId',
        element: <EditFooterStyleOption />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Display Pricings
      {
        path: '/system/display-pricings',
        element: <DisplayPricingList />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/display-pricings/add',
        element: <AddDisplayPricing />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/display-pricings/edit/:pricingId',
        element: <EditDisplayPricing />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Display Pricings
      {
        path: '/system/display-pricings-config',
        element: <DisplayPricingsConfig />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Survey Email Automations
      {
        path: '/system/survey-emails-config',
        element: <SurveyEmailsConfig />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Static Pricings Config
      {
        path: '/system/backwrap-sidevent-pricing-config',
        element: <BackWrapSideVentPricingForm />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/cabinet-pricing-config',
        element: <CabinetPricingForm />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/pedestal-pricing-config',
        element: <PedestalPricingForm />,
        exact: true,
        roles: [ROLES.Admin]
      },
      {
        path: '/system/mow-curb-pricing-config',
        element: <MowCurbPricingForm />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // Emc Vendors
      {
        path: '/system/emc-vendors',
        element: <EmcVendorList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/emc-vendors/add',
        element: <AddEmcVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/emc-vendors/edit/:vendorId',
        element: <EditEmcVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      // Fabrication Vendors
      {
        path: '/system/fabrication-vendors',
        element: <FabricationVendorList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/fabrication-vendors/add',
        element: <AddFabricationVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/fabrication-vendors/edit/:vendorId',
        element: <EditFabricationVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      // Installation Vendors
      {
        path: '/system/installation-vendors',
        element: <InstallationVendorList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/installation-vendors/add',
        element: <AddInstallationVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/installation-vendors/edit/:vendorId',
        element: <EditInstallationVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      // Shipping Vendors
      {
        path: '/system/shipping-vendors',
        element: <ShippingVendorList />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/shipping-vendors/add',
        element: <AddShippingVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      {
        path: '/system/shipping-vendors/edit/:vendorId',
        element: <EditShippingVendor />,
        exact: true,
        roles: [ROLES.Admin, ROLES.ProjectManager]
      },
      // Commission
      {
        path: '/sales/commission',
        element: <Commission isMyCommission={false} />,
        exact: true,
        roles: [ROLES.Admin]
      },
      // My Commission
      {
        path: '/sales/my-commission',
        element: <Commission isMyCommission={true} />,
        exact: true,
        roles: [ROLES.SalesManager, ROLES.SalesUser]
      },
      // Overage
      {
        path: '/sales/overage',
        element: <Overage />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      // Report History (Commission, Overage)
      {
        path: '/sales/report-history',
        element: <ReportHistory />,
        exact: true,
        roles: [ROLES.Admin, ROLES.SalesManager, ROLES.SalesUser]
      },
      // Metrics
      {
        path: '/reporting/metrics',
        element: <Metrics />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.SalesUser && ROLES.ClientUser && ROLES.GraphicDesigner))
      },
      // Metrics Task Due
      {
        path: '/reporting/metrics/task-due',
        element: <MetricsTaskDue />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.SalesUser && ROLES.ClientUser && ROLES.GraphicDesigner))
      },
      // Metrics No Activity
      {
        path: '/reporting/metrics/no-activity',
        element: <MetricsNoActivity />,
        exact: true,
        roles: arrRoles.filter(v => (v !== ROLES.SalesUser && ROLES.ClientUser && ROLES.GraphicDesigner))
      }
    ]
  }, [])
}
